// src/pages/admin/AdminMatchingModels.jsx

import React, { useEffect, useState } from 'react';
import { matchingModelAPI } from '../../services/api';
import { Spinner, useConfirm } from '../../components/shared/UI';
import toast from 'react-hot-toast';
import { Plus, Pencil, Trash2, X, ChevronDown, ChevronRight, Shield, AlertTriangle } from 'lucide-react';

// ── Weight Slider Row ────────────────────────────────────
const WeightRow = ({ dmp, weight, isHardFilter, onWeightChange, onHardFilterChange }) => {
  const pct = Math.round(weight * 100);
  return (
    <div style={{
      padding: '10px 12px', borderRadius: 6, border: '1px solid #eee',
      background: isHardFilter ? '#FFF3F3' : '#fafafa',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 500 }}>{dmp.name}</span>
          {isHardFilter && (
            <span style={{
              fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 3,
              background: '#CC0000', color: 'white', textTransform: 'uppercase',
            }}>
              Hard Filter
            </span>
          )}
        </div>
        <span style={{ fontSize: 16, fontWeight: 700, color: '#3D80CC', fontFamily: 'monospace' }}>{pct}%</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <input type="range" min="0" max="100" step="5" value={pct}
          onChange={e => onWeightChange(parseInt(e.target.value) / 100)}
          style={{ flex: 1, cursor: 'pointer' }} />
        <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontSize: 11, color: '#777' }}>
          <input type="checkbox" checked={isHardFilter} onChange={e => onHardFilterChange(e.target.checked)} />
          Hard filter
        </label>
      </div>
      {dmp.fdtaLinks && (
        <div style={{ marginTop: 6, fontSize: 10, color: '#888' }}>
          Fields: {dmp.fdtaLinks.map(l => l.fdta.name).join(', ')}
        </div>
      )}
    </div>
  );
};

// ── MEM Form Modal ───────────────────────────────────────
const MemModal = ({ open, onClose, onSave, initial, allDmps, saving }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [dmpConfigs, setDmpConfigs] = useState([]); // [{ dmpId, weight, isHardFilter }]

  useEffect(() => {
    if (open) {
      if (initial) {
        setName(initial.name);
        setDescription(initial.description || '');
        setDmpConfigs(
          (initial.dmpLinks || []).map(l => ({
            dmpId: l.dmp.id,
            weight: l.weight,
            isHardFilter: l.isHardFilter,
          }))
        );
      } else {
        setName(''); setDescription(''); setDmpConfigs([]);
      }
    }
  }, [open, initial]);

  if (!open) return null;

  const selectedIds = new Set(dmpConfigs.map(d => d.dmpId));
  const totalWeight = dmpConfigs.reduce((sum, d) => sum + d.weight, 0);
  const totalPct = Math.round(totalWeight * 100);

  const toggleDmp = (dmpId) => {
    if (selectedIds.has(dmpId)) {
      setDmpConfigs(dmpConfigs.filter(d => d.dmpId !== dmpId));
    } else {
      setDmpConfigs([...dmpConfigs, { dmpId, weight: 0, isHardFilter: false }]);
    }
  };

  const updateWeight = (dmpId, weight) => {
    setDmpConfigs(dmpConfigs.map(d => d.dmpId === dmpId ? { ...d, weight } : d));
  };

  const updateHardFilter = (dmpId, isHardFilter) => {
    setDmpConfigs(dmpConfigs.map(d => d.dmpId === dmpId ? { ...d, isHardFilter } : d));
  };

  const canSave = name.trim() && dmpConfigs.length > 0;

  const handleSave = () => {
    onSave({
      name: name.trim(),
      description: description.trim() || null,
      dmps: dmpConfigs,
    });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" style={{ maxWidth: 600, maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}
        onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 style={{ margin: 0 }}>{initial ? 'Edit' : 'Create'} Engine Model</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'white' }}>
            <X size={18} />
          </button>
        </div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16, overflowY: 'auto', flex: 1 }}>

          <div>
            <label className="form-label">Model Name <span style={{ color: '#CC0000', marginLeft: 2 }}>*</span></label>
            <input className="form-input" value={name} onChange={e => setName(e.target.value)}
              placeholder="e.g. Standard Matching v1" />
          </div>

          <div>
            <label className="form-label">Description</label>
            <input className="form-input" value={description} onChange={e => setDescription(e.target.value)}
              placeholder="Optional description" />
          </div>

          {/* DMP Selection */}
          <div>
            <label className="form-label">
              Select Matching Pairs <span style={{ color: '#CC0000', marginLeft: 2 }}>*</span>
            </label>
            {allDmps.length === 0 ? (
              <div style={{ fontSize: 12, color: '#999', padding: 8 }}>
                No matching pairs available. Create some first.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {allDmps.map(dmp => {
                  const isSelected = selectedIds.has(dmp.id);
                  return (
                    <label key={dmp.id} style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px',
                      borderRadius: 6, cursor: 'pointer',
                      background: isSelected ? '#E8F4FD' : 'transparent',
                      border: `1px solid ${isSelected ? '#3D80CC' : '#eee'}`,
                    }}>
                      <input type="checkbox" checked={isSelected} onChange={() => toggleDmp(dmp.id)} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 12, fontWeight: 500 }}>{dmp.name}</div>
                        {dmp.description && (
                          <div style={{ fontSize: 10, color: '#888' }}>{dmp.description}</div>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          {/* Weight Configuration */}
          {dmpConfigs.length > 0 && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <label className="form-label" style={{ margin: 0 }}>Weight Allocation</label>
                <span style={{
                  fontSize: 14, fontWeight: 700, fontFamily: 'monospace',
                  padding: '2px 10px', borderRadius: 6,
                  color: totalPct === 100 ? '#28A745' : '#CC0000',
                  background: totalPct === 100 ? '#E8F5E9' : '#FFEBEE',
                  border: `1px solid ${totalPct === 100 ? '#28A745' : '#CC0000'}33`,
                }}>
                  {totalPct}%
                </span>
              </div>

              {totalPct !== 100 && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px',
                  background: '#FFF8E1', border: '1px solid #FFE082', borderRadius: 6,
                  fontSize: 11, color: '#8B6914', marginBottom: 8,
                }}>
                  <AlertTriangle size={14} />
                  Weights should total 100% for accurate scoring.
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {dmpConfigs.map(config => {
                  const dmp = allDmps.find(d => d.id === config.dmpId);
                  if (!dmp) return null;
                  return (
                    <WeightRow
                      key={config.dmpId}
                      dmp={dmp}
                      weight={config.weight}
                      isHardFilter={config.isHardFilter}
                      onWeightChange={(w) => updateWeight(config.dmpId, w)}
                      onHardFilterChange={(hf) => updateHardFilter(config.dmpId, hf)}
                    />
                  );
                })}
              </div>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={!canSave || saving}>
            {saving ? <Spinner size={14} /> : (initial ? 'Update' : 'Create')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Main Page ────────────────────────────────────────────
export const AdminMatchingModels = () => {
  const [mems, setMems] = useState([]);
  const [dmps, setDmps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState({});
  const { confirm, dialog } = useConfirm();

  const load = async () => {
    try {
      const [memRes, dmpRes] = await Promise.all([
        matchingModelAPI.getMems(),
        matchingModelAPI.getDmps(),
      ]);
      setMems(memRes.data.data);
      setDmps(dmpRes.data.data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleSave = async (payload) => {
    setSaving(true);
    try {
      if (editing) {
        await matchingModelAPI.updateMem(editing.id, payload);
        toast.success('Model updated');
      } else {
        await matchingModelAPI.createMem(payload);
        toast.success('Model created');
      }
      setModalOpen(false);
      setEditing(null);
      await load();
    } catch {}
    setSaving(false);
  };

  const handleDelete = async (mem) => {
    if (!await confirm('Delete Engine Model', `Delete "${mem.name}"? This cannot be undone.`)) return;
    try {
      await matchingModelAPI.deleteMem(mem.id);
      toast.success('Deleted');
      await load();
    } catch {}
  };

  const toggleExpand = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 64 }}><Spinner size={32} /></div>;

  return (
    <div style={{ maxWidth: 900 }}>
      <div className="page-header" style={{ marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h1 style={{ margin: 0, fontSize: 18 }}>Engine Models</h1>
        <button className="btn btn-primary" style={{ fontSize: 12 }}
          onClick={() => { setEditing(null); setModalOpen(true); }}>
          <Plus size={14} style={{ marginRight: 4 }} /> New Model
        </button>
      </div>

      <p style={{ fontSize: 13, color: '#555', marginBottom: 16 }}>
        Engine models bring together matching pairs with weight allocations to define a complete
        matching strategy. Each pair can be weighted to control its contribution to the overall score,
        and optionally marked as a hard filter (score = 0 excludes the candidate).
      </p>

      {mems.length === 0 ? (
        <div className="section-box">
          <div className="section-box-body" style={{ textAlign: 'center', padding: 32, color: '#999' }}>
            No engine models yet. Create matching pairs first, then build models here.
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {mems.map(mem => {
            const isExpanded = expanded[mem.id];
            const totalPct = Math.round((mem.dmpLinks || []).reduce((s, l) => s + l.weight, 0) * 100);
            const hardFilters = (mem.dmpLinks || []).filter(l => l.isHardFilter);

            return (
              <div key={mem.id} className="section-box">
                <div className="section-box-header"
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', padding: '8px 12px' }}
                  onClick={() => toggleExpand(mem.id)}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    <span style={{ fontWeight: 600 }}>{mem.name}</span>
                    <span style={{ fontSize: 11, opacity: 0.7 }}>
                      ({(mem.dmpLinks || []).length} pairs)
                    </span>
                    <span style={{
                      fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 3,
                      background: totalPct === 100 ? 'rgba(40,167,69,0.2)' : 'rgba(204,0,0,0.2)',
                      color: totalPct === 100 ? '#28A745' : '#ffcccc',
                    }}>
                      {totalPct}%
                    </span>
                    {hardFilters.length > 0 && (
                      <span style={{ fontSize: 10, opacity: 0.7 }}>
                        <Shield size={10} style={{ marginRight: 2 }} />
                        {hardFilters.length} hard filter{hardFilters.length > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 4 }} onClick={e => e.stopPropagation()}>
                    <button title="Edit" onClick={() => { setEditing(mem); setModalOpen(true); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'white', padding: 4 }}>
                      <Pencil size={14} />
                    </button>
                    <button title="Delete" onClick={() => handleDelete(mem)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ffcccc', padding: 4 }}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                {isExpanded && (
                  <div className="section-box-body" style={{ padding: '12px 16px' }}>
                    {mem.description && (
                      <div style={{ fontSize: 12, color: '#666', marginBottom: 12, fontStyle: 'italic' }}>
                        {mem.description}
                      </div>
                    )}

                    {/* Weight bar visualisation */}
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: '#3D80CC', textTransform: 'uppercase', marginBottom: 4 }}>
                        Weight Distribution
                      </div>
                      <div style={{ display: 'flex', height: 16, borderRadius: 4, overflow: 'hidden', gap: 1, background: '#eee' }}>
                        {(mem.dmpLinks || []).map((l, i) => {
                          const pct = Math.round(l.weight * 100);
                          if (pct === 0) return null;
                          const colors = ['#3D80CC', '#28A745', '#FF9800', '#9C27B0', '#E91E63', '#00BCD4', '#795548'];
                          return (
                            <div key={l.id}
                              title={`${l.dmp.name}: ${pct}%`}
                              style={{ width: `${pct}%`, background: colors[i % colors.length], transition: 'width 0.3s' }}
                            />
                          );
                        })}
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
                        {(mem.dmpLinks || []).map((l, i) => {
                          const colors = ['#3D80CC', '#28A745', '#FF9800', '#9C27B0', '#E91E63', '#00BCD4', '#795548'];
                          return (
                            <div key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#555' }}>
                              <div style={{ width: 8, height: 8, borderRadius: 2, background: colors[i % colors.length] }} />
                              {l.dmp.name} {Math.round(l.weight * 100)}%
                              {l.isHardFilter && <Shield size={10} color="#CC0000" />}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* DMP details */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {(mem.dmpLinks || []).map(l => (
                        <div key={l.id} style={{
                          padding: '8px 12px', borderRadius: 6, border: '1px solid #eee',
                          background: l.isHardFilter ? '#FFF3F3' : '#fafafa',
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ fontSize: 13, fontWeight: 500 }}>{l.dmp.name}</span>
                              {l.isHardFilter && (
                                <span style={{
                                  fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 3,
                                  background: '#CC0000', color: 'white', textTransform: 'uppercase',
                                }}>Hard Filter</span>
                              )}
                            </div>
                            <span style={{ fontSize: 14, fontWeight: 700, color: '#3D80CC', fontFamily: 'monospace' }}>
                              {Math.round(l.weight * 100)}%
                            </span>
                          </div>
                          {l.dmp.fdtaLinks && (
                            <div style={{ marginTop: 4, fontSize: 10, color: '#888' }}>
                              CV: {l.dmp.fdtaLinks.filter(fl => fl.fdta.side === 'CV').map(fl => fl.fdta.name).join(', ') || 'none'}
                              {' | '}
                              Position: {l.dmp.fdtaLinks.filter(fl => fl.fdta.side === 'POSITION').map(fl => fl.fdta.name).join(', ') || 'none'}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <MemModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setEditing(null); }}
        onSave={handleSave}
        initial={editing}
        allDmps={dmps}
        saving={saving}
      />
      {dialog}
    </div>
  );
};
